import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Input,
  Button,
  Badge,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  IconButton,
  Flex,
  Divider,
  List,
  ListItem,
  ListIcon,
  useClipboard,
  Spinner,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FiShare2,
  FiCopy,
  FiCheck,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiArrowLeft,
  FiRefreshCw,
  FiDownload,
  FiMessageCircle,
  FiAlertTriangle,
  FiInfo,
  FiCode,
  FiTwitter,
  FiMessageSquare,
  FiLink,
  FiUserPlus,
  FiUserCheck,
  FiClock,
  FiCheckCircle,
} from 'react-icons/fi';
import { useAccount, useContractRead, useContractWrite, useWaitForTransaction } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useAppSelector } from '@/store';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';

import { formatAddress, formatUSD, formatDOR } from '@/utils/formatters';

// 模拟邀请历史数据
const mockInviteHistory = [
  {
    id: 1,
    user: '0x1234...5678',
    rechargeAmount: 100,
    returnAmount: 100,
    timestamp: '2025-07-29 10:30',
    status: '已完成',
    txHash: '0xabc...def',
  },
  {
    id: 2,
    user: '0x8765...4321',
    rechargeAmount: 50,
    returnAmount: 50,
    timestamp: '2025-07-28 15:45',
    status: '已完成',
    txHash: '0xdef...abc',
  },
  {
    id: 3,
    user: '0x1111...2222',
    rechargeAmount: 200,
    returnAmount: 200,
    timestamp: '2025-07-27 09:15',
    status: '待处理',
    txHash: '0x111...222',
  },
];

// 模拟返现历史图表数据
const mockReturnHistory = [
  { date: '2025-07-25', returns: 0, invites: 0 },
  { date: '2025-07-26', returns: 0, invites: 0 },
  { date: '2025-07-27', returns: 200, invites: 1 },
  { date: '2025-07-28', returns: 250, invites: 2 },
  { date: '2025-07-29', returns: 350, invites: 3 },
];

const Invite: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('invite');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');



  // 状态管理
  const [activeTab, setActiveTab] = useState(0);
  const [inviteCode, setInviteCode] = useState('DOR123456');
  const [inputCode, setInputCode] = useState('');
  const [isValidCode, setIsValidCode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isBinding, setIsBinding] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 邀请统计
  const inviteStats = {
    totalInvites: 3,
    totalReturns: 350,
    remainingLocked: 150,
    unlockProgress: 70, // 70% 已解锁
  };

  // 复制功能
  const { onCopy: onCopyCode, hasCopied: hasCopiedCode } = useClipboard(inviteCode);
  const { onCopy: onCopyLink, hasCopied: hasCopiedLink } = useClipboard(`https://dor.app/invite?code=${inviteCode}`);

  // 生成邀请码
  const handleGenerateCode = async () => {
    setIsGenerating(true);
    try {
      // 模拟合约调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      const newCode = 'DOR' + Math.random().toString(36).substr(2, 6).toUpperCase();
      setInviteCode(newCode);
      
      toast({
        title: t('generateInvite.generateSuccess'),
        description: `${t('generateInvite.generateSuccessDesc')}: ${newCode}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('generateInvite.generateFailed'),
        description: t('generateInvite.generateFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // 验证邀请码
  const validateCode = async (code: string) => {
    if (!code || code.length < 6) {
      setIsValidCode(false);
      return;
    }

    try {
      // 模拟验证
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsValidCode(code.startsWith('DOR'));
    } catch (error) {
      setIsValidCode(false);
    }
  };

  // 绑定邀请码
  const handleBindCode = async () => {
    if (!isValidCode) return;

    setIsBinding(true);
    try {
      // 模拟合约调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: t('useInviteCode.bindSuccess'),
        description: t('useInviteCode.bindSuccessDesc'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // 跳转到充值页面
      navigate('/recharge');
    } catch (error) {
      toast({
        title: t('useInviteCode.bindFailed'),
        description: t('useInviteCode.bindFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsBinding(false);
    }
  };

  // 刷新历史
  const handleRefreshHistory = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: t('inviteHistory.refreshSuccess'),
        description: t('inviteHistory.refreshSuccessDesc'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('inviteHistory.refreshFailed'),
        description: t('inviteHistory.refreshFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // 分享到社交媒体
  const shareToTwitter = () => {
    const text = `加入 DOR 稳定币，充值获双倍奖励！我的邀请码: ${inviteCode}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareToTelegram = () => {
    const text = `加入 DOR 稳定币，充值获双倍奖励！我的邀请码: ${inviteCode}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent('https://dor.app/invite?code=' + inviteCode)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // 导出历史
  const exportHistory = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "用户,充值金额,返现金额,时间,状态\n"
      + mockInviteHistory.map(item => 
          `${item.user},${item.rechargeAmount},${item.returnAmount},${item.timestamp},${item.status}`
        ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "invite_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: t('inviteHistory.exportSuccess'),
      description: t('inviteHistory.exportSuccessDesc'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // 如果未连接钱包，显示连接提示
  if (!isConnected) {
    return (
      <Box>
        <VStack spacing={6} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />
          
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody textAlign="center" py={12}>
              <Box mb={4}><FiUsers size={64} color="var(--chakra-colors-blue-500)" /></Box>
              <Heading size="md" mb={2}>
                {t('connectWalletPrompt')}
              </Heading>
              <Text color="gray.500" mb={6}>
                {t('connectWalletDescription')}
              </Text>
              <ConnectButton />
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* 顶部区域 */}
        <PageHeader title="邀请与 Referral 系统" />

        {/* 概览提示 */}
        <Alert 
          status={inviteStats.totalInvites > 0 ? "info" : "warning"} 
          borderRadius="md"
          mb={4}
        >
          <AlertIcon />
          <Box>
            <AlertTitle>
              {inviteStats.totalInvites > 0 
                ? `${t('alerts.inviteChainActive')}: ${inviteStats.totalInvites} ${t('inviteStats.people')}`
                : t('alerts.startInvitingFriends')
              }
            </AlertTitle>
            <AlertDescription>
              {inviteStats.totalInvites > 0 
                ? `${t('alerts.returnedAmount')}: ${formatUSD(inviteStats.totalReturns)}，${t('alerts.remainingLocked')}: ${formatDOR(inviteStats.remainingLocked)}`
                : t('alerts.inviteDescription')
              }
            </AlertDescription>
          </Box>
        </Alert>

        {/* 主体内容区 */}
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
          <TabList>
            <Tab>{t('tabGenerateInvite')}</Tab>
            <Tab>{t('tabUseInviteCode')}</Tab>
            <Tab>{t('tabInviteHistory')}</Tab>
          </TabList>

          <TabPanels>
            {/* 生成邀请模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">{t('generateInvite.title')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      {/* 当前邀请码 */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>{t('generateInvite.yourCode')}</Text>
                        <HStack>
                          <Input
                            value={inviteCode}
                            isReadOnly
                            size="lg"
                            fontFamily="mono"
                          />
                          <Button
                            onClick={onCopyCode}
                            leftIcon={hasCopiedCode ? <FiCheck /> : <FiCopy />}
                            size="lg"
                          >
                            {hasCopiedCode ? t('copied') : t('copy')}
                          </Button>
                        </HStack>
                      </Box>

                      {/* 生成新码按钮 */}
                      <Button
                        colorScheme="blue"
                        leftIcon={<FiUserPlus />}
                        onClick={handleGenerateCode}
                        isLoading={isGenerating}
                        loadingText={t('generateInvite.generating')}
                      >
                        {t('generateInvite.generateNew')}
                      </Button>

                      {/* 分享工具 */}
                      <Divider />
                      <Box>
                        <Text fontWeight="bold" mb={4}>{t('generateInvite.shareTools')}</Text>
                        
                        {/* QR码 */}
                        <VStack spacing={4} mb={6}>
                          <Text fontSize="sm" color="gray.500">{t('generateInvite.qrCode')}</Text>
                          <Box p={4} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                            <QRCode
                              value={`https://dor.app/invite?code=${inviteCode}`}
                              size={120}
                              level="M"
                            />
                          </Box>
                          <Text fontSize="xs" color="gray.400">{t('generateInvite.scanToJoin')}</Text>
                        </VStack>

                        {/* 分享链接 */}
                        <VStack spacing={4} mb={6}>
                          <Text fontSize="sm" color="gray.500">{t('generateInvite.shareLink')}</Text>
                          <HStack w="full">
                            <Input
                              value={`https://dor.app/invite?code=${inviteCode}`}
                              isReadOnly
                              size="md"
                            />
                            <Button
                              onClick={onCopyLink}
                              leftIcon={hasCopiedLink ? <FiCheck /> : <FiCopy />}
                              size="md"
                            >
                              {hasCopiedLink ? t('copied') : t('copy')}
                            </Button>
                          </HStack>
                        </VStack>

                        {/* 社交分享按钮 */}
                        <HStack spacing={3}>
                          <Button
                            leftIcon={<FiTwitter />}
                            colorScheme="blue"
                            variant="outline"
                            onClick={shareToTwitter}
                          >
                            {t('generateInvite.shareToX')}
                          </Button>
                          <Button
                            leftIcon={<FiMessageSquare />}
                            colorScheme="blue"
                            variant="outline"
                            onClick={shareToTelegram}
                          >
                            {t('generateInvite.shareToTelegram')}
                          </Button>
                        </HStack>
                      </Box>

                      {/* 激励提示 */}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>{t('generateInvite.incentiveTitle')}</AlertTitle>
                          <AlertDescription>
                            {t('generateInvite.incentiveDesc')}
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* 使用邀请码模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">{t('useInviteCode.title')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      {/* 输入邀请码 */}
                      <Box>
                        <Text fontWeight="bold" mb={2}>{t('useInviteCode.enterCode')}</Text>
                        <Input
                          value={inputCode}
                          onChange={(e) => {
                            setInputCode(e.target.value);
                            validateCode(e.target.value);
                          }}
                          placeholder={t('useInviteCode.enterCodePlaceholder')}
                          size="lg"
                          fontFamily="mono"
                        />
                      </Box>

                      {/* 验证结果 */}
                      {inputCode && (
                        <Alert 
                          status={isValidCode ? "success" : "error"} 
                          borderRadius="md"
                        >
                          <AlertIcon />
                          <Box>
                            <AlertTitle>
                              {isValidCode ? t('useInviteCode.validCode') : t('useInviteCode.invalidCode')}
                            </AlertTitle>
                            <AlertDescription>
                              {isValidCode 
                                ? t('useInviteCode.inviterInfo')
                                : t('useInviteCode.checkCode')
                              }
                            </AlertDescription>
                          </Box>
                        </Alert>
                      )}

                      {/* 绑定按钮 */}
                      <Button
                        colorScheme="blue"
                        leftIcon={<FiUserCheck />}
                        onClick={handleBindCode}
                        isLoading={isBinding}
                        loadingText={t('useInviteCode.binding')}
                        isDisabled={!isValidCode}
                      >
                        {t('useInviteCode.bindCode')}
                      </Button>

                      {/* 益处说明 */}
                      <Box>
                        <Text fontWeight="bold" mb={3}>{t('useInviteCode.benefitsTitle')}</Text>
                        <List spacing={2}>
                          <ListItem>
                            <FiCheckCircle color="green.500" />
                            {t('useInviteCode.benefit1')}
                          </ListItem>
                          <ListItem>
                            <FiCheckCircle color="green.500" />
                            {t('useInviteCode.benefit2')}
                          </ListItem>
                          <ListItem>
                            <FiCheckCircle color="green.500" />
                            {t('useInviteCode.benefit3')}
                          </ListItem>
                        </List>
                      </Box>

                      {/* 替代选项 */}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>{t('useInviteCode.noCodeTitle')}</AlertTitle>
                          <AlertDescription>
                            {t('useInviteCode.noCodeDesc')}
                            <Button
                              variant="link"
                              colorScheme="blue"
                              ml={2}
                              onClick={() => navigate('/recharge')}
                            >
                              {t('useInviteCode.rechargeNow')}
                            </Button>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* 邀请历史模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">{t('inviteHistory.title')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={6} align="stretch">
                      {/* 统计摘要 */}
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                        <Stat>
                          <StatLabel>{t('inviteHistory.stats.invitedPeople')}</StatLabel>
                          <StatNumber color="blue.500">
                            {inviteStats.totalInvites}
                          </StatNumber>
                          <StatHelpText>
                            <StatArrow type="increase" />
                            {t('inviteHistory.stats.activeUsers')}
                          </StatHelpText>
                        </Stat>

                        <Stat>
                          <StatLabel>{t('inviteHistory.stats.totalReturns')}</StatLabel>
                          <StatNumber color="green.500">
                            {formatUSD(inviteStats.totalReturns)}
                          </StatNumber>
                          <StatHelpText>
                            <StatArrow type="increase" />
                            {t('inviteHistory.stats.cumulativeEarnings')}
                          </StatHelpText>
                        </Stat>

                        <Stat>
                          <StatLabel>{t('inviteHistory.stats.remainingLocked')}</StatLabel>
                          <StatNumber color="orange.500">
                            {formatDOR(inviteStats.remainingLocked)}
                          </StatNumber>
                          <StatHelpText>
                            {t('inviteHistory.stats.unlockProgress')}: {inviteStats.unlockProgress}%
                          </StatHelpText>
                        </Stat>
                      </SimpleGrid>

                      {/* 解锁进度条 */}
                      <Box>
                        <HStack justify="space-between" mb={2}>
                          <Text fontSize="sm">{t('inviteHistory.unlockProgressTitle')}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {inviteStats.unlockProgress}%
                          </Text>
                        </HStack>
                        <Progress 
                          value={inviteStats.unlockProgress} 
                          colorScheme="green" 
                          size="lg"
                          borderRadius="full"
                        />
                      </Box>

                      {/* 返现历史图表 */}
                      <Box h="200px">
                        <Text fontWeight="bold" mb={4}>{t('inviteHistory.returnTrend')}</Text>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={mockReturnHistory}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <RechartsTooltip />
                            <Line 
                              type="monotone" 
                              dataKey="returns" 
                              stroke="#38A169" 
                              strokeWidth={2}
                              name={t('inviteHistory.stats.totalReturns') + ' (USDT)'}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="invites" 
                              stroke="#3182CE" 
                              strokeWidth={2}
                              name={t('inviteHistory.stats.invitedPeople')}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* 邀请历史表格 */}
                      <Box>
                        <HStack justify="space-between" mb={4}>
                          <Text fontWeight="bold">{t('inviteHistory.detailedRecords')}</Text>
                          <HStack spacing={2}>
                            <Button
                              leftIcon={<FiDownload />}
                              size="sm"
                              variant="outline"
                              onClick={exportHistory}
                            >
                              {t('inviteHistory.exportHistory')}
                            </Button>
                            <Button
                              leftIcon={<FiMessageCircle />}
                              size="sm"
                              variant="outline"
                            >
                              {t('inviteHistory.remindInvitees')}
                            </Button>
                          </HStack>
                        </HStack>

                        <Box overflowX="auto">
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th>{t('inviteHistory.table.invitee')}</Th>
                                <Th>{t('inviteHistory.table.rechargeAmount')}</Th>
                                <Th>{t('inviteHistory.table.returnUSDT')}</Th>
                                <Th>{t('inviteHistory.table.time')}</Th>
                                <Th>{t('inviteHistory.table.status')}</Th>
                                <Th>{t('inviteHistory.table.action')}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {mockInviteHistory.map((item) => (
                                <Tr key={item.id}>
                                  <Td>
                                    <Text fontFamily="mono" fontSize="sm">
                                      {item.user}
                                    </Text>
                                  </Td>
                                  <Td>{formatUSD(item.rechargeAmount)}</Td>
                                  <Td color="green.500">{formatUSD(item.returnAmount)}</Td>
                                  <Td>{item.timestamp}</Td>
                                  <Td>
                                    <Badge 
                                      colorScheme={item.status === t('inviteHistory.table.completed') ? 'green' : 'yellow'}
                                      size="sm"
                                    >
                                      {item.status === '已完成' ? t('inviteHistory.table.completed') : t('inviteHistory.table.pending')}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => window.open(`https://testnet.bscscan.com/tx/${item.txHash}`, '_blank')}
                                    >
                                      {t('inviteHistory.table.view')}
                                    </Button>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* 底部行动区 */}
        <HStack justify="center" spacing={4}>
          <Button
            leftIcon={<FiShare2 />}
            colorScheme="blue"
            onClick={() => {
              onCopyLink();
              toast({
                title: t('actions.linkCopied'),
                description: t('actions.linkCopiedDesc'),
                status: 'success',
                duration: 2000,
                isClosable: true,
              });
            }}
          >
            {t('actions.shareInviteLink')}
          </Button>

        </HStack>

        {/* 风险免责横幅 */}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('riskDisclaimer')}</AlertTitle>
            <AlertDescription>
              {t('riskDescription')}
            </AlertDescription>
          </Box>
        </Alert>

        {/* 确认绑定模态 */}
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modals.confirmBindTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>{t('modals.confirmBindMessage')}</Text>
              <Alert status="info">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('modals.potentialBenefits')}</AlertTitle>
                  <AlertDescription>
                    {t('modals.benefitsDescription')}
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleBindCode}>
              {t('confirmAssociate')}
            </Button>
          </ModalFooter>
        </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default Invite; 